import React from 'react';
import { Activity, ArrowRight, Clock, Code } from 'lucide-react';
import type { DetrixActionLine, DetrixSession } from '../../types';
import { ErrorNarrative } from '../../components/ErrorNarrative';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatSessionOffset } from '../../utils/time';

interface ActionInspectorProps {
  session: DetrixSession;
  action: DetrixActionLine;
}

export const ActionInspector: React.FC<ActionInspectorProps> = ({ session, action }) => {
  const { setActiveTab, setSelectedId } = useSessionUiStore();

  const handleJumpToEvent = () => {
    setActiveTab('events');
    setSelectedId(action.eventId);
  };

  const isErr = action.kind === 'error';

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
      <ErrorNarrative session={session} />
      {isErr ? (
        <div className="shrink-0 border-b border-[var(--inspector-error-border)] bg-[var(--inspector-error-bg)] px-3 py-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--inspector-error-title)]">Action error</div>
          <p className="mt-1 text-[12px] text-[var(--inspector-error-body)]">{action.summary}</p>
        </div>
      ) : null}
      <div className="border-b p-3">
        <div className="mb-1 flex items-center gap-2">
          <Activity className={`h-4 w-4 ${isErr ? 'text-[var(--error)]' : 'text-[var(--accent)]'}`} />
          <h3
            className={`text-[14px] font-semibold uppercase ${isErr ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}
          >
            {action.kind}
          </h3>
        </div>
        <p className="text-[11px] text-[var(--text-secondary)]">{action.summary}</p>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden p-3">
        <section>
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
            <Clock className="h-3 w-3" /> Timing
          </h4>
          <div className="space-y-1">
            <KVRow label="Timestamp" value={formatSessionOffset(action.timestamp)} />
            <KVRow label="Raw (ms)" value={String(action.timestamp)} />
          </div>
        </section>

        <section>
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
            <Code className="h-3 w-3" /> Source event
          </h4>
          <button
            type="button"
            onClick={handleJumpToEvent}
            className="group flex w-full items-center justify-between rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
          >
            <span className="font-mono text-[var(--text-secondary)]">{action.eventId}</span>
            <div className="flex items-center gap-1 text-[var(--accent)] opacity-0 transition-opacity group-hover:opacity-100">
              <span>Jump</span>
              <ArrowRight className="h-3 w-3" />
            </div>
          </button>
        </section>
      </div>
    </div>
  );
};

const KVRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-2 font-mono text-[11px]">
    <span className="w-[100px] shrink-0 text-[var(--text-secondary)]">{label}:</span>
    <span className="text-[var(--text-primary)]">{value}</span>
  </div>
);
